/**
 * Column-reorder math + the table-applying glue for the drag-and-drop header (U5), isolated
 * from the DnD event plumbing so both are deterministically unit-testable (jsdom can't
 * faithfully simulate a `@dnd-kit` pointer drag — the real gesture is covered by Storybook /
 * browser-qa, but the glue below is plain JS and fully testable).
 */
import { arrayMove } from '@dnd-kit/sortable'
import type { RowData, Table } from '@tanstack/react-table'

/**
 * Move `activeId` to `overId`'s slot within the column order. Operates on the FULL leaf
 * order (falling back to `defaultOrder` before the first reorder) so pinned columns keep
 * their array slots — they render by pin state, so only the center order visibly moves.
 * Returns `null` for a no-op (same slot, or an id not in the order) so the caller can skip
 * a redundant state write.
 */
export const computeReorderedColumnOrder = (
  defaultOrder: string[],
  currentOrder: string[],
  activeId: string,
  overId: string,
): string[] | null => {
  const base = currentOrder.length > 0 ? currentOrder : defaultOrder
  const oldIndex = base.indexOf(activeId)
  const newIndex = base.indexOf(overId)

  if (oldIndex === -1 || newIndex === -1) {
    // A drag/move targeting an id the engine doesn't know is a real desync between `@dnd-kit`'s
    // tracked items and the column order — surface it (dev-only) instead of hiding it behind a
    // no-op-shaped null, which would look identical to an ordinary drag-to-same-spot.
    if (import.meta.env.DEV) {
      console.warn(
        `computeReorderedColumnOrder: column id not found in order (active="${activeId}", over="${overId}") — reorder skipped.`,
      )
    }

    return null
  }

  if (oldIndex === newIndex) {
    return null
  }

  return arrayMove(base, oldIndex, newIndex)
}

/**
 * Apply a reorder to the live table: move `activeId` to `overId`'s slot in the full leaf order
 * and commit via `setColumnOrder` (a no-op when the move resolves to nothing). Shared by the
 * drag-end handler and the keyboard "Move left/right" menu items.
 */
export const applyColumnReorder = <TData extends RowData>(
  table: Table<TData>,
  activeId: string,
  overId: string,
): void => {
  const nextOrder = computeReorderedColumnOrder(
    table.getAllLeafColumns().map((column) => column.id),
    table.getState().columnOrder,
    activeId,
    overId,
  )
  if (nextOrder !== null) {
    table.setColumnOrder(nextOrder)
  }
}

/** The drag-end seam: guard a null/self drop, coerce the `@dnd-kit` ids, then apply the move. */
export const reorderColumnsFromDragEnd = <TData extends RowData>(
  table: Table<TData>,
  event: { active: { id: string | number }; over: { id: string | number } | null },
): void => {
  if (event.over === null || event.active.id === event.over.id) {
    return
  }
  applyColumnReorder(table, String(event.active.id), String(event.over.id))
}
